import Alpine from 'alpinejs';

interface ListboxOption {
    value: string;
    label: string;
}

interface ListboxState {
    open: boolean;
    selected: ListboxOption | null;
    ready: boolean;
    activeIndex: number;
    searchString: string;
    searchTimeout: ReturnType<typeof setTimeout> | null;
    $refs: {
        trigger: HTMLButtonElement;
        listbox: HTMLUListElement;
    };
    $el: HTMLElement;
    $dispatch: (event: string, detail?: unknown) => void;
    $nextTick: (callback: () => void) => void;
    isActive: boolean;
    toggle: () => void;
    select: (option: ListboxOption) => void;
    onKeydown: (e: KeyboardEvent) => void;
    handleTypeAhead: (char: string) => void;
    scrollActiveIntoView: () => void;
    init: () => void;
}

const PAGE_JUMP_SIZE = 10;
const SEARCH_TIMEOUT_MS = 500;

export function registerListbox(): void {
    Alpine.data('listbox', (initialSelected: ListboxOption | null = null): ListboxState => ({
        open: false,
        selected: initialSelected,
        ready: false,
        activeIndex: 0,
        searchString: '',
        searchTimeout: null,
        $refs: {} as ListboxState['$refs'],
        $el: {} as HTMLElement,
        $dispatch: () => {},
        $nextTick: () => {},

        get isActive(): boolean {
            return this.open || this.selected !== null;
        },

        toggle(): void {
            this.open = !this.open;
            if (this.open) {
                const items = Array.from(this.$refs.listbox.children) as HTMLElement[];
                const selectedIdx = items.findIndex(
                    (el) => el.dataset.value === this.selected?.value
                );
                this.activeIndex = selectedIdx >= 0 ? selectedIdx : 0;
                this.$nextTick(() => this.$refs.listbox.focus());
            }
        },

        select(option: ListboxOption): void {
            this.selected = option;
            this.open = false;
            this.$refs.trigger.focus();
            this.$dispatch('listbox-change', option);
        },

        onKeydown(e: KeyboardEvent): void {
            const count = this.$refs.listbox.children.length;

            // Type-ahead: handle printable characters
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                this.handleTypeAhead(e.key);
                return;
            }

            switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.activeIndex = (this.activeIndex + 1) % count;
                this.scrollActiveIntoView();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.activeIndex = (this.activeIndex - 1 + count) % count;
                this.scrollActiveIntoView();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                (this.$refs.listbox.children[this.activeIndex] as HTMLElement)?.click();
                break;
            case 'Escape':
                e.preventDefault();
                this.open = false;
                this.$refs.trigger.focus();
                break;
            case 'Home':
                e.preventDefault();
                this.activeIndex = 0;
                this.scrollActiveIntoView();
                break;
            case 'End':
                e.preventDefault();
                this.activeIndex = count - 1;
                this.scrollActiveIntoView();
                break;
            case 'PageDown':
                e.preventDefault();
                this.activeIndex = Math.min(this.activeIndex + PAGE_JUMP_SIZE, count - 1);
                this.scrollActiveIntoView();
                break;
            case 'PageUp':
                e.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - PAGE_JUMP_SIZE, 0);
                this.scrollActiveIntoView();
                break;
            case 'Tab':
                // Allow default behavior - close listbox and move focus to next element
                this.open = false;
                break;
            }
        },

        handleTypeAhead(char: string): void {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.searchString += char.toLowerCase();

            const options = Array.from(this.$refs.listbox.children) as HTMLElement[];
            const count = options.length;

            // Search from position after current, wrapping around
            for (let i = 0; i < count; i++) {
                const idx = (this.activeIndex + 1 + i) % count;
                const label = options[idx].textContent?.trim().toLowerCase() || '';

                if (label.startsWith(this.searchString)) {
                    this.activeIndex = idx;
                    this.scrollActiveIntoView();
                    break;
                }
            }

            // Reset search string after timeout
            this.searchTimeout = setTimeout(() => {
                this.searchString = '';
            }, SEARCH_TIMEOUT_MS);
        },

        scrollActiveIntoView(): void {
            this.$nextTick(() => {
                const activeOption = this.$refs.listbox.children[this.activeIndex] as HTMLElement;
                if (activeOption) {
                    activeOption.scrollIntoView({ block: 'nearest' });
                }
            });
        },

        init(): void {
            this.$nextTick(() => {
                this.ready = true;
            });
        },
    }));
}
